import { useMemo } from 'react';

import {
    DEFAULT_Y_AXIS_VALUES,
    MAX_PERCENTAGE,
    MIN_PERCENTAGE,
    calculateMaxAxisTick,
    calculateMinAxisTick,
    generateYAxisValues,
    getBadgeColor,
    getBadgeTextColor,
    sortByPercentageDescending,
} from '../utils/chartCalculations';

import type { BarChartValue } from '../../BarChart/BarChart';
import type { RankingOptionAnswer } from '../RankingQuestionResult';

// --- Types ---
interface YAxisConfig {
    yValues: number[];
    maxYValue: number;
    minYValue: number;
}

interface UseRankingChartDataReturn {
    barValues: BarChartValue[];
    yAxisConfig: YAxisConfig;
}

// --- Helper Functions ---
const calculatePercentage = (count: number, maxCount: number): number => {
    const hasValidMaxCount = maxCount > 0;

    if (!hasValidMaxCount) {
        return 0;
    }

    return Math.round((count / maxCount) * 100);
};

// --- Main Hook ---
/**
 * Custom hook to calculate ranking chart data
 * Handles percentage calculations, Y-axis configuration, and bar value transformations
 *
 * @param options - Array of ranking options with counts
 * @returns Chart data ready for rendering
 */
export const useRankingChartData = (options: RankingOptionAnswer[]): UseRankingChartDataReturn => {
    const maxCount = useMemo(() => {
        const counts = options.map((option) => option.count);

        return Math.max(...counts, 0);
    }, [options]);

    const percentages = useMemo(() => {
        return options.map((option) => calculatePercentage(option.count, maxCount));
    }, [options, maxCount]);

    const yAxisConfig = useMemo((): YAxisConfig => {
        const hasNoData = percentages.length === 0;

        if (hasNoData) {
            return {
                yValues: DEFAULT_Y_AXIS_VALUES,
                maxYValue: MAX_PERCENTAGE,
                minYValue: MIN_PERCENTAGE,
            };
        }

        const validPercentages = percentages.filter((percentage) => percentage >= 0);
        const minPercentage = Math.min(...validPercentages);
        const maxPercentage = Math.max(...validPercentages);

        const minYValue = calculateMinAxisTick(minPercentage, MIN_PERCENTAGE);
        const maxYValue = calculateMaxAxisTick(maxPercentage, MAX_PERCENTAGE);
        const yValues = generateYAxisValues(minYValue, maxYValue);

        return { yValues, maxYValue, minYValue };
    }, [percentages]);

    const barValues: BarChartValue[] = useMemo(() => {
        const indexedPercentages = percentages.map((percentage, index) => ({
            percentage,
            index,
        }));

        const sortedByPercentage = [...indexedPercentages].sort(sortByPercentageDescending);
        const sortedIndices = sortedByPercentage.map((item) => item.index);

        return sortedIndices.map((originalIndex, displayIndex) => {
            const option = options[originalIndex];
            const percentage = percentages[originalIndex];

            return {
                label: option.label,
                value: percentage,
                badgeColor: getBadgeColor(displayIndex),
                badgeTextColor: getBadgeTextColor(displayIndex),
            };
        });
    }, [options, percentages]);

    return { barValues, yAxisConfig };
};
